/**
 * RegistrationModeToggle Component (AC-921)
 *
 * Displays current registration mode and allows toggling
 * between open and invite_only with a confirmation dialog.
 *
 * Props:
 *   currentMode: 'open' | 'invite_only'
 *   onToggle: (mode: 'open' | 'invite_only') => Promise<void>
 *   isLoading: boolean
 *
 * Epic: AC-915 - Soft-Launch Email Allowlist with Admin Portal
 */

import React, { useState } from 'react';
import ConfirmationDialog from '../common/ConfirmationDialog';

interface RegistrationModeToggleProps {
  currentMode: 'open' | 'invite_only';
  onToggle: (mode: 'open' | 'invite_only') => Promise<void>;
  isLoading: boolean;
}

const RegistrationModeToggle: React.FC<RegistrationModeToggleProps> = ({
  currentMode,
  onToggle,
  isLoading,
}) => {
  const [showConfirm, setShowConfirm] = useState(false);

  const targetMode = currentMode === 'open' ? 'invite_only' : 'open';

  const handleToggleClick = () => {
    if (isLoading) return;
    setShowConfirm(true);
  };

  const handleConfirm = async () => {
    await onToggle(targetMode);
    setShowConfirm(false);
  };

  const handleCancel = () => {
    setShowConfirm(false);
  };

  const getConfirmMessage = () => {
    if (currentMode === 'open') {
      return 'Switching to Invitation Only will restrict registration to users on the allowlist. Only users with an allowed email address will be able to register.';
    }
    return 'Switching to Open Registration will allow anyone to register for an account without restriction.';
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-warm-white">
            Registration Mode
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {currentMode === 'open' ? (
              <span>Current mode: <strong>Open Registration</strong> - Anyone can register.</span>
            ) : (
              <span>Current mode: <strong>Invitation Only</strong> - Only allowlisted emails can register.</span>
            )}
          </p>
        </div>

        <button
          onClick={handleToggleClick}
          disabled={isLoading}
          data-testid="mode-toggle"
          aria-label="Change mode"
          className="btn btn-primary bg-primary-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Change Mode
        </button>
      </div>

      {showConfirm && (
        <ConfirmationDialog
          title="Change Registration Mode"
          message={getConfirmMessage()}
          confirmText="Confirm"
          cancelText="Cancel"
          confirmVariant="warning"
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      )}
    </div>
  );
};

export default RegistrationModeToggle;
